import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BackendService, HttpOptions } from '@ipg/ngx-frontend-toolset';
import { Group } from '../models/group.model';
import { MediaContext, groupsBasePath } from '../models/media-context.model';

const UNIT_CTX: MediaContext = { scope: 'unit' };

@Injectable({
  providedIn: 'root',
})
export class GroupsService {
  private readonly _backend = inject(BackendService);

  create(data: unknown, options?: HttpOptions, ctx: MediaContext = UNIT_CTX): Observable<string> {
    return this._backend.post(groupsBasePath(ctx), data, options);
  }

  update(id: string, data: unknown, options?: HttpOptions, ctx: MediaContext = UNIT_CTX): Observable<void> {
    return this._backend.put(`${groupsBasePath(ctx)}/${id}`, data, options);
  }

  getAll(options?: HttpOptions, ctx: MediaContext = UNIT_CTX): Observable<Group[]> {
    return this._backend.get(groupsBasePath(ctx), options);
  }

  getById(id: string, options?: HttpOptions, ctx: MediaContext = UNIT_CTX): Observable<Group> {
    return this._backend.get(`${groupsBasePath(ctx)}/${id}`, options);
  }

  /**
   * Resolve a set of media-group ids to lightweight name records. The backend
   * scopes the result to the caller's unit and omits ids it may not view, so
   * the response only contains groups the user is allowed to see.
   */
  getByIds(ids: string[], options?: HttpOptions): Observable<Group[]> {
    return this._backend.get<Group[]>('/groups/by-ids', {
      ...options,
      params: { ...options?.params, ids },
    });
  }

  getList(
    ctx: MediaContext = UNIT_CTX,
    extraParams?: Record<string, string>,
  ): Observable<Group[]> {
    const options = extraParams ? { params: extraParams } : undefined;
    if (ctx.scope === 'client') {
      return this._backend.get(groupsBasePath(ctx), options);
    }
    return this._backend.get('/groups/list', options);
  }

  moveTo(
    {
      sourceGroupIds,
      targetGroupId,
    }: {
      sourceGroupIds: string[];
      targetGroupId: string | null;
    },
    ctx: MediaContext = UNIT_CTX,
  ) {
    return this._backend.put(`${groupsBasePath(ctx)}/move`, { sourceGroupIds, targetGroupId });
  }
}
